/**
 * SQL Accounting Integration Page
 * Real-time sync with SQL Accounting System
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Database, RefreshCw, CheckCircle2, XCircle, AlertCircle,
  FileText, ShoppingCart, Users, Package, BookOpen, UserCheck,
  TrendingUp, TrendingDown, Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SQLAccountingPage = () => {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  
  // Detail data states
  const [activeTab, setActiveTab] = useState('overview');
  const [salesInvoices, setSalesInvoices] = useState([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch status and summary
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statusRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/sql-acc/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sql-acc/summary`, { withCredentials: true })
      ]);
      
      setStatus(statusRes.data);
      setSummary(summaryRes.data.summary);
      setLastSync(new Date().toLocaleString('ms-MY'));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch detail data based on active tab
  const fetchDetailData = useCallback(async (tab) => {
    setDetailLoading(true);
    try {
      let response;
      switch (tab) {
        case 'sales':
          response = await axios.get(`${API_URL}/api/sql-acc/sales-invoices`, { withCredentials: true });
          setSalesInvoices(response.data.invoices || []);
          break;
        case 'purchases':
          response = await axios.get(`${API_URL}/api/sql-acc/purchase-invoices`, { withCredentials: true });
          setPurchaseInvoices(response.data.invoices || []);
          break;
        case 'customers':
          response = await axios.get(`${API_URL}/api/sql-acc/customers`, { withCredentials: true });
          setCustomers(response.data.customers || []);
          break;
        case 'suppliers':
          response = await axios.get(`${API_URL}/api/sql-acc/suppliers`, { withCredentials: true });
          setSuppliers(response.data.suppliers || []);
          break;
        case 'stock':
          response = await axios.get(`${API_URL}/api/sql-acc/stock-items`, { withCredentials: true });
          setStockItems(response.data.items || []);
          break;
        case 'agents':
          response = await axios.get(`${API_URL}/api/sql-acc/agents`, { withCredentials: true });
          setAgents(response.data.agents || []);
          break;
        case 'gl':
          response = await axios.get(`${API_URL}/api/sql-acc/gl-accounts`, { withCredentials: true });
          setGLAccounts(response.data.accounts || []);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`Error fetching ${tab} data:`, err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab !== 'overview') {
      fetchDetailData(activeTab);
    }
  }, [activeTab, fetchDetailData]);

  // Manual sync
  const handleSync = async () => {
    setSyncing(true);
    await fetchData();
    if (activeTab !== 'overview') {
      await fetchDetailData(activeTab);
    }
    setSyncing(false);
  };

  // Summary cards data
  const summaryCards = [
    { 
      key: 'sales_invoices', 
      label: 'Sales Invoices', 
      icon: TrendingUp, 
      color: 'bg-emerald-500',
      tab: 'sales'
    },
    { 
      key: 'purchase_invoices', 
      label: 'Purchase Invoices', 
      icon: TrendingDown, 
      color: 'bg-red-500',
      tab: 'purchases'
    },
    { 
      key: 'customers', 
      label: 'Customers', 
      icon: Users, 
      color: 'bg-blue-500',
      tab: 'customers'
    },
    { 
      key: 'suppliers', 
      label: 'Suppliers', 
      icon: ShoppingCart, 
      color: 'bg-orange-500',
      tab: 'suppliers'
    },
    { 
      key: 'stock_items', 
      label: 'Stock Items', 
      icon: Package, 
      color: 'bg-purple-500',
      tab: 'stock'
    },
    { 
      key: 'agents', 
      label: 'Agents', 
      icon: UserCheck, 
      color: 'bg-cyan-500',
      tab: 'agents'
    },
    { 
      key: 'gl_accounts', 
      label: 'GL Accounts', 
      icon: BookOpen, 
      color: 'bg-amber-500',
      tab: 'gl'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'sales', label: 'Sales' },
    { id: 'purchases', label: 'Purchases' },
    { id: 'customers', label: 'Customers' },
    { id: 'suppliers', label: 'Suppliers' },
    { id: 'stock', label: 'Stock' },
    { id: 'agents', label: 'Agents' },
    { id: 'gl', label: 'GL Accounts' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-red-700 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Loading SQL Accounting data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sql-accounting-page">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
              <Database className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">SQL Accounting</h1>
              <p className="text-gray-500">Real-time sync dengan SQL Accounting System</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              status?.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}>
              {status?.configured ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Connected</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Not Connected</span>
                </>
              )}
            </div>
            
            {/* Sync Button */}
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              data-testid="sync-btn"
            >
              <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        </div>
        
        {lastSync && (
          <p className="text-sm text-gray-400 mt-3">
            Last sync: {lastSync}
          </p>
        )}
        
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-700">Connection Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200 overflow-x-auto">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {summaryCards.map((card) => {
                const Icon = card.icon;
                const data = summary?.[card.key];
                return (
                  <button
                    key={card.key}
                    onClick={() => setActiveTab(card.tab)}
                    className="bg-gray-50 hover:bg-gray-100 rounded-xl p-4 text-left transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 ${card.color} rounded-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-medium text-gray-700">{card.label}</span>
                    </div>
                    <div className="flex items-end justify-between">
                      <span className="text-3xl font-bold text-gray-800">
                        {data?.error ? '-' : data?.count || 0}
                      </span>
                      {data?.error && (
                        <AlertCircle className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    {data?.error && (
                      <p className="text-xs text-red-500 mt-2 truncate">{data.error}</p>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Data Tables */}
          {activeTab !== 'overview' && (
            <div>
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Sales Invoices */}
                  {activeTab === 'sales' && (
                    <DataTable
                      data={salesInvoices}
                      columns={['docno', 'docdate', 'code', 'companyname', 'amount', 'description']}
                      emptyMessage="Tiada sales invoice"
                    />
                  )}
                  
                  {/* Purchase Invoices */}
                  {activeTab === 'purchases' && (
                    <DataTable
                      data={purchaseInvoices}
                      columns={['docno', 'docdate', 'code', 'companyname', 'amount', 'description']}
                      emptyMessage="Tiada purchase invoice"
                    />
                  )}
                  
                  {/* Customers */}
                  {activeTab === 'customers' && (
                    <DataTable
                      data={customers}
                      columns={['code', 'companyname', 'attention', 'phone1', 'email']}
                      emptyMessage="Tiada customer"
                    />
                  )}
                  
                  {/* Suppliers */}
                  {activeTab === 'suppliers' && (
                    <DataTable
                      data={suppliers}
                      columns={['code', 'companyname', 'attention', 'phone1', 'email']}
                      emptyMessage="Tiada supplier"
                    />
                  )}
                  
                  {/* Stock Items */}
                  {activeTab === 'stock' && (
                    <DataTable
                      data={stockItems}
                      columns={['code', 'description', 'stockgroup', 'baseuom', 'refprice', 'refcost']}
                      emptyMessage="Tiada stock item"
                    />
                  )}
                  
                  {/* Agents */}
                  {activeTab === 'agents' && (
                    <DataTable
                      data={agents}
                      columns={['code', 'description', 'phone', 'email']}
                      emptyMessage="Tiada agent"
                    />
                  )}
                  
                  {/* GL Accounts */}
                  {activeTab === 'gl' && (
                    <DataTable
                      data={glAccounts}
                      columns={['code', 'description', 'specialaccttype', 'taxtype']}
                      emptyMessage="Tiada GL account"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Reusable Data Table Component
const DataTable = ({ data, columns, emptyMessage }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Format column header
  const formatHeader = (col) => {
    return col
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace('companyname', 'Company Name')
      .replace('docno', 'Doc No')
      .replace('docdate', 'Date')
      .replace('phone1', 'Phone')
      .replace('baseuom', 'UOM')
      .replace('refprice', 'Price')
      .replace('refcost', 'Cost')
      .replace('stockgroup', 'Group')
      .replace('specialaccttype', 'Type')
      .replace('taxtype', 'Tax');
  };

  return (
    <table className="w-full">
      <thead>
        <tr className="bg-gray-50">
          {columns.map((col) => (
            <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              {formatHeader(col)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {data.slice(0, 50).map((row, idx) => (
          <tr key={idx} className="hover:bg-gray-50">
            {columns.map((col) => (
              <td key={col} className="px-4 py-3 text-sm text-gray-700">
                {row[col] || '-'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default SQLAccountingPage;
